import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, Subject, Subscription, combineLatest, debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs';
import { CvBox } from 'src/app/cv-box/shared/models/cv-box-model';
import { theme } from 'src/environments/theme';
import { Announcement } from '../shared/models/announcement-model';
import { select, Store } from '@ngrx/store';
import { AnnouncementStoreState } from 'src/app/root-store/announcement-store/state';
import { loadFrontPageCvBoxes } from 'src/app/root-store/cv-box-store/actions/cv-box-front-page-actions';
import {
  getFrontPageBannerList,
  loadFrontPageAnnouncements,
  loadFrontPageMetaData,
  showCvBoxSlider,
} from 'src/app/root-store/announcement-store/announcement-front-page-actions';
import {
  loadLastListingPage,
  sendBannerViewToAnalytic,
} from 'src/app/root-store/actions';
import { PageNameEnum } from 'src/app/shared/models/enums/page-names-enum';
import {
  orderedAnnouncementListIds,
  selectFrontAnnouncementPage,
  selectFrontPageBannerIds,
  selectFrontPageBottomBanner,
  selectFrontPageCityLinks,
  selectFrontPageMetaData,
  selectFrontPageMidBanner,
  selectFrontPageMidBanner2,
  selectFrontPageTopBanner,
  selectHasMorePages,
  selectIsFrontPageLinkVisible,
  selectIsLoading,
  selectIsMainPaigCvBoxSliderVisible,
  selectMergedAnnouncements,
  selectVipAnnouncements,
  selectVipCvAnnouncements,
} from 'src/app/root-store/announcement-store/selectors';
import { MetaService } from 'src/app/shared/services/meta.service';
import { FrontPageCityLink } from '../shared/services/announcement.service';
import {
  selectAllPremiumCvBoxes,
  selectAllVipCvBoxes,
} from 'src/app/root-store/cv-box-store/selectors';
import { AnnouncementFilter } from 'src/app/shared/models/announcement-filter';
import { AnnouncementService } from 'src/app/shared/services/announcement.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BannerTypeEnum } from 'src/app/banner/shared/models/enums/banner-type-enum';
import { BannerTargetEnum } from 'src/app/banner/shared/models/enums/banner-target-enum';
import { Banner } from 'src/app/banner/shared/models/banner-model';
import { BannerPositionEnum } from 'src/app/banner/shared/models/enums/banner-position-enum';
import { Breakpoints } from 'src/app/shared/models/breakpoints-model';
import { SsrService } from '@hrra/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { getOwlOptions } from 'src/app/shared/helpers/cv-box-slider-otions ';
import {
  selectCurrentUserType,
  selectCurrentWebsite,
} from 'src/app/root-store/selectors';
import { BannerHeaderPositionId } from 'src/app/shared/models/enums/banner-header-position-id-enum';
import { DeviceDetectionService, EnvService } from '@hrra/core';

@Component({
    selector: 'app-front-page',
    templateUrl: `./${theme.name}/./front-page.component.html`,
    styleUrls: [`./${theme.name}/front-page.component.scss`],
    standalone: false
})
export class MainPageComponent implements OnInit, OnDestroy {
  metaDataSubscription: Subscription;

  date = new Date();

  private destroy$ = new Subject<void>();

  pageId: any;

  targetId: number = BannerTargetEnum.Desktop;

  vipCvAnnouncements$: Observable<Announcement[]>;
  vipAnnouncements$: Observable<Announcement[]>;

  providerWebsiteId: number;

  public currentPage$: Observable<number>

  selectHasMorePages$: Observable<boolean>;

  mergedAnnouncements$: Observable<Announcement[]>;

  selectFrontPageCityLinks$: Observable<FrontPageCityLink[]>;

  frontPageTopBanner$: Observable<Banner>;
  frontPageMidBanner$: Observable<Banner>;
  frontPageMidBanner2$: Observable<Banner>;
  frontPageBottomBanner$: Observable<Banner>;

  vipCvBoxes$: Observable<CvBox[]>;
  premiumCvBoxes$: Observable<CvBox[]>;

  isLoading$: Observable<boolean>;
  isFrontPageLinkVisible$: Observable<boolean>;
  isCvBoxSliderVisible$: Observable<boolean>;

  bannerType: any;
  cvBoxSliderOptions: OwlOptions;

  pageName: any;

  bannerHeaderId: any;

  userType$: Observable<any>;

  constructor(
    private store: Store<AnnouncementStoreState>,
    private metaService: MetaService,
    private announcementService: AnnouncementService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private envService: EnvService,
    private ssr: SsrService,
    private deviceDetectionService: DeviceDetectionService
  ) {
    this.bannerHeaderId = BannerHeaderPositionId;
    this.pageName = PageNameEnum;
    this.cvBoxSliderOptions = getOwlOptions();
    this.bannerType = BannerTypeEnum;

    this.pageId = BannerHeaderPositionId;
  }

  ngOnInit(): void {

    this.providerWebsiteId = this.envService.tenantId

    const deviceInfo = this.deviceDetectionService.detectDevice();

    this.targetId = deviceInfo.isDesktop || deviceInfo.isTablet ? BannerTargetEnum.Desktop : BannerTargetEnum.Responsive;
    
    let frontPageBanners$ = this.store.select(selectFrontPageBannerIds);
    let providerWebsiteId$ = this.store.select(selectCurrentWebsite);

    combineLatest([frontPageBanners$, providerWebsiteId$])
      .pipe(
        filter(([banners, provderWebsite]) => {
          return (
            banners.bannerIdList.length > 0 &&
            provderWebsite !== null &&
            provderWebsite !== undefined
          );
        }),
        take(1)
      )
      .subscribe(([b, p]) => {
        this.store.dispatch(
          sendBannerViewToAnalytic({
            payload: {
              bannerIdList: b,
              providerWebsiteId: p.providerWebsiteId,
              pageId: BannerHeaderPositionId.FrontPage,
            },
          })
        );
      });

    this.store.dispatch(
      loadLastListingPage({ payload: PageNameEnum.FrontPage })
    );
    this.isCvBoxSliderVisible$ = this.store.pipe(
      select(selectIsMainPaigCvBoxSliderVisible)
    );

    if (this.ssr.isPlatformBrowser()) {
      this.store.dispatch(
        getFrontPageBannerList({
          payload: {
            targetId: this.targetId,
            bannerHeadPositionId: BannerPositionEnum.Main_page,
          },
        })
      );
      this.store.dispatch(showCvBoxSlider({ payload: true }));

      // A fresh visit always starts loading from page 1, so drop any shared
      // ?page=N param to keep the URL in sync with the actual flow. On back
      // navigation the state is already populated (page > 1) and the restored
      // URL is left untouched. replaceState avoids adding a history entry.
      this.store
        .select(selectFrontAnnouncementPage)
        .pipe(take(1))
        .subscribe((loadedPage) => {
          if (loadedPage === 1) {
            this.location.replaceState(this.buildPageUrl(null));
          }
        });
    }

    this.store.dispatch(
      loadFrontPageAnnouncements({
        payload: { page: 1, targetId: deviceInfo.isMobile ? BannerTargetEnum.Responsive : BannerTargetEnum.Desktop }, //TODO
    }));

    this.store.dispatch(loadFrontPageMetaData())

    this.store.dispatch(loadFrontPageCvBoxes());

    this.metaDataSubscription = this.store
      .pipe(select(selectFrontPageMetaData))
      .subscribe((data) => {
        this.metaService.updateMetaData(data.website, data.metaData);
      });

    this.vipCvAnnouncements$ = this.store.select(selectVipCvAnnouncements);

    this.currentPage$ = this.store.select(selectFrontAnnouncementPage);

    this.vipAnnouncements$ = this.store.select(
      selectVipAnnouncements
    );
    this.mergedAnnouncements$ = this.store.select(selectMergedAnnouncements);

    this.selectFrontPageCityLinks$ = this.store.select(
      selectFrontPageCityLinks
    );

    this.selectHasMorePages$ = this.store.select(selectHasMorePages);

    this.frontPageTopBanner$ = this.store.select(selectFrontPageTopBanner);
    this.frontPageMidBanner$ = this.store.select(selectFrontPageMidBanner);
    this.frontPageMidBanner2$ = this.store.select(selectFrontPageMidBanner2);
    this.frontPageBottomBanner$ = this.store.select(
      selectFrontPageBottomBanner
    );

    this.vipCvBoxes$ = this.store.select(selectAllVipCvBoxes);
    this.premiumCvBoxes$ = this.store.select(selectAllPremiumCvBoxes);

    this.isLoading$ = this.store.pipe(select(selectIsLoading));
    this.isFrontPageLinkVisible$ = this.store.pipe(
      select(selectIsFrontPageLinkVisible)
    );

    this.userType$ = this.store.select(selectCurrentUserType);
  }

  announcementId(index: number, announcement: Announcement) {
    return announcement.announcementId;
  }

  cityLinkId(index: number, cityLink: FrontPageCityLink) {
    return cityLink.localityId;
  }

  cvBoxId(index: number, cvBox: CvBox) {
    return cvBox.customerId;
  }

  onLocalityLinkClick(localityOption: number) {
    let searchData = <AnnouncementFilter>{
      localityIds: [localityOption],
    };

    let q = this.announcementService.convertLocalityOptionToQueryParams(searchData);

    this.router.navigate(['./search-posting'], {
      queryParams: q,
    });
  }

  ngOnDestroy() {
    this.metaDataSubscription.unsubscribe();
    this.destroy$.next(null);
    this.destroy$.complete()
  }

  onScrollDown(hasMorePages: boolean, currentPage: number) {
    if (hasMorePages) {
      this.store.dispatch(
        loadFrontPageAnnouncements({
          payload: { page: currentPage, targetId: this.targetId },
        })

      );

      // Reflect the loaded page in the URL via a real history push so the
      // top.ge counter (which patches history.pushState) registers it as a
      // view. location.go() updates the URL without an Angular router
      // navigation, so the list isn't reloaded and the scroll position is
      // preserved (router scrollPositionRestoration is 'enabled').
      this.location.go(this.buildPageUrl(currentPage));
    }
    // this.store.dispatch(addAnnouncementsInVipSectionList());
  }

  // Serializes the current route with the page query param set (or removed
  // when null). The param only reflects scroll progress for sharing and the
  // counter; it never drives data loading, which always begins at page 1.
  private buildPageUrl(page: number | null): string {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { page },
      queryParamsHandling: 'merge',
    });

    return this.router.serializeUrl(urlTree);
  }

  // onScrollDownMergedAnn(ev) {
  //   this.store.dispatch(addAnnouncementsInMergedSectionList());
  // }
}
function loadFroAddAnnouncementsInVipSectionListntPageCvBoxes(): any {
  throw new Error('Function not implemented.');
}
